import * as Redux from 'redux';
import * as Bixby from './../../core';
import {
  CompanyState,
  CompanyActionTypes,
  CREATE_COMPANY,
  REMOVE_COMPANY,
  SET_CURRENT_COMPANY,
  COMPANY_LOADING,
  COMPANY_ERROR,
  COMPANIES_LOADING,
  COMPANIES_SUCCESS,
  COMPANIES_FAILURE,
  COMPANIES_CLEAR,
  ADD_CLIENT_COMPANY,
  ADD_CLIENT_COMPANY_SUCCESS,
  ADD_CLIENT_COMPANY_FAILURE,
  DELETE_CLIENT_COMPANY,
  DELETE_CLIENT_COMPANY_SUCCESS,
  DELETE_CLIENT_COMPANY_FAILURE,
  GENERATE_PRIMER,
  GENERATE_PRIMER_SUCCESS,
  GENERATE_PRIMER_FAILURE,
  GENERATE_TEARSHEET,
  GENERATE_TEARSHEET_SUCCESS,
  GENERATE_TEARSHEET_FAILURE,
  ADD_CLIENT_COMPANY_TASK,
  ADD_CLIENT_COMPANY_TASK_SUCCESS,
  ADD_CLIENT_COMPANY_TASK_FAILURE,
  ADD_PUBLIC_COMPANY_TASK,
  ADD_PUBLIC_COMPANY_TASK_SUCCESS,
  ADD_PUBLIC_COMPANY_TASK_FAILURE,
  DOCUMENT_ANNOUNCEMENT_LOADING,
  DOCUMENT_ANNOUNCEMENT_SUCCESS,
  DOCUMENT_ANNOUNCEMENT_FAILURE,
  MOST_RECENT_COMPANY_TASK_LOADING,
  MOST_RECENT_COMPANY_TASK_SUCCESS,
  MOST_RECENT_COMPANY_TASK_FAILURE,
  IMPORT_COMPANIES,
  IMPORT_COMPANIES_SUCCESS,
  IMPORT_COMPANIES_FAILURE,
} from './types';
import { LOGIN_CLEAR_PRELOADED_DATA } from '$/store/login/types';
import { AdminCompany } from '$/core/models';

const initialState: CompanyState = {
  isLoading: false,
  error: undefined,
  currentCompany: {} as Bixby.Models.AdminCompany,
  companies: undefined,
  isLoadingDocumentAnnouncement: false,
  isLoadingMostRecentCompanyTask: false,
  addingCompanyToClientError: undefined,
  mostRecentCompanyTask: undefined,
};

const companyReducer: Redux.Reducer<CompanyState> = (
  state = initialState,
  action: CompanyActionTypes
): CompanyState => {
  switch (action.type) {
    case LOGIN_CLEAR_PRELOADED_DATA:
      return initialState;
    case COMPANIES_LOADING:
      return {
        ...state,
        isLoading: true,
        companies: undefined,
      };
    case COMPANIES_SUCCESS:
      const keyofName: keyof Bixby.Models.AdminCompanySlim = 'name';
      const payload: Bixby.Models.AdminCompanySlim[] = Bixby.Utils.orderBy(
        (action.payload as Bixby.Models.AdminCompanySlim[]) || [],
        keyofName
      );
      return {
        ...state,
        isLoading: false,
        companies: payload,
      };
    case COMPANIES_FAILURE:
      return {
        ...state,
        error: action.payload as Bixby.Models.BixbyError,
        isLoading: false,
        companies: undefined,
      };
    case SET_CURRENT_COMPANY:
      const company = action.payload as Bixby.Models.AdminCompany;
      return {
        ...state,
        currentCompany: company,
        isLoading: false,
      };
    case CREATE_COMPANY:
      const newCompany = action.payload as Bixby.Models.AdminCompany;
      return {
        ...state,
        currentCompany: newCompany,
        isLoading: false,
      };
    case REMOVE_COMPANY:
      return {
        ...state,
      };
    case COMPANIES_CLEAR:
      return {
        ...state,
        companies: undefined,
      };
    case COMPANY_LOADING:
      return {
        ...state,
        isLoading: true,
      };
    case COMPANY_ERROR:
      return {
        ...state,
        error: action.payload as Bixby.Models.BixbyError,
        isLoading: false,
      };
    case ADD_CLIENT_COMPANY:
      return {
        ...state,
        isAddingCompanyToClient: true,
      };
    case ADD_CLIENT_COMPANY_SUCCESS:
      return {
        ...state,
        isAddingCompanyToClient: false,
      };
    case ADD_CLIENT_COMPANY_FAILURE:
      return {
        ...state,
        isAddingCompanyToClient: false,
        addingCompanyToClientError: (action.payload || {}) as Bixby.Models.BixbyError,
      };
    case DELETE_CLIENT_COMPANY:
      return {
        ...state,
        isDeletingCompanyFromClient: true,
      };
    case DELETE_CLIENT_COMPANY_SUCCESS:
      return {
        ...state,
        isDeletingCompanyFromClient: false,
      };
    case DELETE_CLIENT_COMPANY_FAILURE:
      return {
        ...state,
        isDeletingCompanyFromClient: false,
        deletingCompanyFromClientError: (action.payload || {}) as Bixby.Models.BixbyError,
      };
    case GENERATE_PRIMER:
      return {
        ...state,
        isGeneratingPrimer: true,
      };
    case GENERATE_PRIMER_SUCCESS:
      return {
        ...state,
        isGeneratingPrimer: false,
      };
    case GENERATE_PRIMER_FAILURE:
      return {
        ...state,
        isGeneratingPrimer: false,
      };
    case GENERATE_TEARSHEET:
      return {
        ...state,
        isGeneratingTearSheet: true,
      };
    case GENERATE_TEARSHEET_SUCCESS:
      return {
        ...state,
        isGeneratingTearSheet: false,
      };
    case GENERATE_TEARSHEET_FAILURE:
      return {
        ...state,
        isGeneratingTearSheet: false,
      };
    case ADD_CLIENT_COMPANY_TASK:
      return {
        ...state,
        isAddingClientCompanyTask: true,
      };
    case ADD_CLIENT_COMPANY_TASK_SUCCESS:
      return {
        ...state,
        isAddingClientCompanyTask: false,
      };
    case ADD_CLIENT_COMPANY_TASK_FAILURE:
      return {
        ...state,
        isAddingClientCompanyTask: false,
      };
    case ADD_PUBLIC_COMPANY_TASK:
      return {
        ...state,
        isAddingPublicCompanyTask: true,
      };
    case ADD_PUBLIC_COMPANY_TASK_SUCCESS:
      return {
        ...state,
        isAddingPublicCompanyTask: false,
      };
    case ADD_PUBLIC_COMPANY_TASK_FAILURE:
      return {
        ...state,
        isAddingPublicCompanyTask: false,
      };
    case DOCUMENT_ANNOUNCEMENT_LOADING:
      return {
        ...state,
        isLoadingDocumentAnnouncement: true,
        error: undefined,
      };
    case DOCUMENT_ANNOUNCEMENT_SUCCESS: {
      const announcement = action.payload as string;
      return {
        ...state,
        currentCompany: {
          ...state.currentCompany as AdminCompany,
          documentAnnouncement: announcement,
        },
        isLoadingDocumentAnnouncement: false,
        error: undefined,
      };
    }
    case DOCUMENT_ANNOUNCEMENT_FAILURE:
      return {
        ...state,
        isLoadingDocumentAnnouncement: false,
        error: action.payload as Bixby.Models.BixbyError,
      };
    case MOST_RECENT_COMPANY_TASK_LOADING:
      return {
        ...state,
        isLoadingMostRecentCompanyTask: true,
        mostRecentCompanyTaskError: undefined,
      };
    case MOST_RECENT_COMPANY_TASK_SUCCESS:
      const mostRecentCompanyTaskPayload = action.payload as Bixby.Models.ClientCompanyTask;
      return {
        ...state,
        isLoadingMostRecentCompanyTask: false,
        mostRecentCompanyTaskError: undefined,
        mostRecentCompanyTask: mostRecentCompanyTaskPayload,
      };
    case MOST_RECENT_COMPANY_TASK_FAILURE:
      return {
        ...state,
        isLoadingMostRecentCompanyTask: false,
        mostRecentCompanyTaskError: action.payload as Bixby.Models.BixbyError,
        mostRecentCompanyTask: undefined,
      };
    case IMPORT_COMPANIES:
      return {
        ...state,
        isImportingCompanies: true,
        importCompaniesError: undefined,
      };
    case IMPORT_COMPANIES_SUCCESS:
      return {
        ...state,
        isImportingCompanies: false,
        importCompaniesError: undefined,
      };
    case IMPORT_COMPANIES_FAILURE:
      return {
        ...state,
        isImportingCompanies: false,
        importCompaniesError: action.payload as Bixby.Models.BixbyError,
      };
    default:
      return state;
  }
};

export default companyReducer;
